/**
 * domainDictionary — FINN organizational terminology for transcription correction
 *
 * Source: Confluence "Talent Frameworks" page + connected data.
 * Used by formatTranscript to correct misheard domain terms.
 *
 * Structure:
 *   - Static terms: hardcoded from Confluence (values, competencies, roles, etc.)
 *   - Dynamic names: loaded from the employees API and cached per session
 */

// ── FINN Core Values ────────────────────────────────────────────────────
export const FINN_VALUES = [
  'Aim High',
  'Own It',
  'Win Together',
  'Embrace Openness',
  'Obsess about Customers',
];

export const VALUE_CATEGORIES = [
  'Growth Mindset', 'Urgency', 'Ambition',
  'Tenacity', 'Ownership', 'Agility',
  'Teamwork', 'FINN first', 'Radical Candor', 'Stakeholder Orientation',
  'Feedback', 'Inclusion',
  'Customer Needs', 'Curiosity',
];

// ── 18 Company-Wide Competencies ────────────────────────────────────────
export const COMPETENCIES = [
  'Advising Customers',
  'Applying Expertise',
  'Building Brand Awareness',
  'Branding',
  'Building Products',
  'Developing Ideas',
  'Executing',
  'Generating Insights',
  'Improving',
  'Influencing',
  'Innovating',
  'Leading Change',
  'Making Deals',
  'Managing Partnerships',
  'Managing Projects',
  'Solving Problems',
  'Steering Performance',
  'Supporting',
];

// ── Leadership Values (used as competencies for leaders) ────────────────
export const LEADERSHIP_VALUES = [
  'Deliver Exceptional Results',
  'Fuel Talent',
  'Push FINN to the Next Level',
];

export const LEADERSHIP_SUB_VALUES = [
  'Talent Management', 'Decisiveness', 'Focus on Impact',
  'Sustainable Performance', 'People Development', 'Learning Culture',
  'Strategic Visioning', 'Innovation', 'FINN First',
];

// ── Job Levels & Tracks ─────────────────────────────────────────────────
export const JOB_LEVELS = [
  'IC1', 'IC2', 'IC3', 'IC4',
  'E5', 'E6', 'E7', 'E8',
  'M5', 'M6', 'M7', 'M8',
];

export const TRACKS = [
  'Individual Contributor', 'IC Track',
  'Expert Track', 'Leadership Track', 'Management Track',
];

// ── Career Path Titles ──────────────────────────────────────────────────
export const TITLE_MODIFIERS = [
  'Associate', 'Senior', 'Senior II', 'Staff', 'Partner',
  'Principal', 'Associate Director', 'Director', 'Senior Director',
  'Distinguished Engineer', 'Executive Director', 'Fellow Engineer',
  'Tech Lead', 'Engineering Manager',
];

// ── FINN-specific Terms ─────────────────────────────────────────────────
export const FINN_TERMS = [
  'FINN', 'Culture Amp', 'Radical Candor', 'OKR', 'OKRs',
  'Talent Framework', 'Talent Frameworks', 'Talent Density',
  'Deliverables', 'Deliverable', 'Competencies', 'Competency',
  'Behaviors', 'Behavior', 'Development Plan', 'Development Goals',
  'Performance Review', 'Performance Rating', 'Calibration',
  'Probation', 'Career Path', 'Career Paths', 'Progression Level',
  'P&O Manager', 'P&O', 'People & Organization',
  'Talent Acquisition', 'SBIR',
  'Situation', 'Behavior', 'Impact', 'Recommendation',
  'Delivery Rating', 'Behavior Rating', 'Overall Performance Rating',
  '70-20-10', 'DRI',
];

// ── All static terms combined (lowercased → canonical form) ─────────────
const _staticMap = new Map<string, string>();

function addTerms(terms: string[]) {
  for (const term of terms) {
    _staticMap.set(term.toLowerCase(), term);
    // Also add individual words for multi-word terms
    const words = term.split(/\s+/);
    if (words.length === 1 && term.length > 3) {
      _staticMap.set(term.toLowerCase(), term);
    }
  }
}

addTerms(FINN_VALUES);
addTerms(VALUE_CATEGORIES);
addTerms(COMPETENCIES);
addTerms(LEADERSHIP_VALUES);
addTerms(LEADERSHIP_SUB_VALUES);
addTerms(JOB_LEVELS);
addTerms(TRACKS);
addTerms(TITLE_MODIFIERS);
addTerms(FINN_TERMS);

// ── Dynamic employee names ──────────────────────────────────────────────
let _employeeNames: Map<string, string> = new Map();

/** Register employee names for transcription correction */
export function setEmployeeNames(names: string[]) {
  _employeeNames = new Map();
  for (const name of names) {
    _employeeNames.set(name.toLowerCase(), name);
    // Also register first and last names individually
    const parts = name.split(/\s+/);
    for (const part of parts) {
      if (part.length > 2) {
        _employeeNames.set(part.toLowerCase(), part);
      }
    }
  }
}

/** Get all registered terms (static + dynamic) */
export function getAllTerms(): Map<string, string> {
  const combined = new Map(_staticMap);
  for (const [k, v] of _employeeNames) {
    combined.set(k, v);
  }
  return combined;
}

/** Get just employee names */
export function getEmployeeNames(): Map<string, string> {
  return new Map(_employeeNames);
}

/**
 * Simple Levenshtein distance for phonetic similarity.
 * Used to match misheard words to known terms.
 */
export function levenshtein(a: string, b: string): number {
  const la = a.length, lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;

  const dp: number[][] = Array.from({ length: la + 1 }, () => Array(lb + 1).fill(0));
  for (let i = 0; i <= la; i++) dp[i][0] = i;
  for (let j = 0; j <= lb; j++) dp[0][j] = j;

  for (let i = 1; i <= la; i++) {
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[la][lb];
}
